import { useEffect, useRef, useState } from 'react'
import { Check, ClipboardList, Plus, RefreshCw, Search, X } from 'lucide-react'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import {
  useSolicitacoes,
  useAprovarSolicitacao,
  useReprovarSolicitacao,
  useLancarRegistroManual,
  usePessoasAjudaCusto,
} from '../hooks/useAjudaCusto'
import { SolicitacaoAjuste, SolicitacaoFiltros, StatusSolicitacao } from '../tipos/ajudaCusto'
import { formatarData } from '../utils'
import SeletorPeriodo from '../../../componentesCompartilhados/periodo/SeletorPeriodo'
import { resolverDatasPeriodo } from '../../../componentesCompartilhados/periodo/resolverDatasPeriodo'
import { usePeriodo } from '../hooks/usePeriodo'

function BadgeStatus({ status, descricao }: { status: StatusSolicitacao; descricao: string }) {
  const cores: Record<number, string> = {
    [StatusSolicitacao.Pendente]: 'bg-yellow-100 text-yellow-800',
    [StatusSolicitacao.Aprovada]: 'bg-green-100 text-green-800',
    [StatusSolicitacao.Reprovada]: 'bg-red-100 text-red-800',
  }
  return <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${cores[status] ?? 'bg-gray-100 text-gray-700'}`}>{descricao}</span>
}

export default function Manutencao() {
  const periodo = usePeriodo('ultimos30')
  const [filtros, setFiltros] = useState<SolicitacaoFiltros>({ status: StatusSolicitacao.Pendente })
  const [filtrosAplicados, setFiltrosAplicados] = useState<SolicitacaoFiltros>(() => {
    const r = resolverDatasPeriodo('ultimos30', '', '', '')
    return { status: StatusSolicitacao.Pendente, dataInicio: r.dataInicio, dataFim: r.dataFim }
  })
  const { data: solicitacoes = [], isLoading, refetch, isFetching } = useSolicitacoes(filtrosAplicados)

  const buscar = () => {
    const r = periodo.resolver()
    setFiltrosAplicados({ status: filtros.status, pessoaId: filtros.pessoaId, dataInicio: r.dataInicio, dataFim: r.dataFim })
  }

  // Ao aplicar o período, refaz a busca automaticamente (pula o primeiro render).
  const primeiraBusca = useRef(true)
  useEffect(() => {
    if (primeiraBusca.current) {
      primeiraBusca.current = false
      return
    }
    buscar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodo.valores.tipoPeriodo, periodo.valores.dataReferencia, periodo.valores.intervaloInicio, periodo.valores.intervaloFim])
  const { data: pessoas = [] } = usePessoasAjudaCusto()

  const aprovar = useAprovarSolicitacao()
  const reprovar = useReprovarSolicitacao()
  const lancarManual = useLancarRegistroManual()

  const [reprovando, setReprovando] = useState<SolicitacaoAjuste | null>(null)
  const [motivo, setMotivo] = useState('')

  const [manualAberto, setManualAberto] = useState(false)
  const [manualPessoa, setManualPessoa] = useState('')
  const [manualData, setManualData] = useState('')

  const confirmarReprova = async () => {
    if (!reprovando || !motivo.trim()) return
    await reprovar.mutateAsync({ id: reprovando.idSolicitacaoAjusteAjudaCusto, motivo: motivo.trim() })
    setReprovando(null)
    setMotivo('')
  }

  const confirmarManual = async () => {
    if (!manualPessoa || !manualData) return
    await lancarManual.mutateAsync({ pessoaId: Number(manualPessoa), data: manualData })
    setManualAberto(false)
    setManualPessoa('')
    setManualData('')
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bônus Presencial — Manutenção</h1>
          <p className="text-gray-600">Aprove ou reprove solicitações de ajuste.</p>
        </div>
        <button
          type="button"
          onClick={() => setManualAberto(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600"
        >
          <Plus className="h-4 w-4" />
          Lançar registro manual
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-theme-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select
              value={filtros.status ?? ''}
              onChange={(e) => setFiltros((f) => ({ ...f, status: e.target.value ? (Number(e.target.value) as StatusSolicitacao) : undefined }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Todos</option>
              <option value={StatusSolicitacao.Pendente}>Pendente</option>
              <option value={StatusSolicitacao.Aprovada}>Aprovada</option>
              <option value={StatusSolicitacao.Reprovada}>Reprovada</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Pessoa</label>
            <select
              value={filtros.pessoaId ?? ''}
              onChange={(e) => setFiltros((f) => ({ ...f, pessoaId: e.target.value ? Number(e.target.value) : undefined }))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Todas</option>
              {pessoas.map((p) => (
                <option key={p.idPessoa} value={p.idPessoa}>{p.nome}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Período</label>
            <SeletorPeriodo {...periodo.seletorProps} />
          </div>
          <button
            type="button"
            onClick={buscar}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600"
          >
            <Search className="h-4 w-4" />
            Buscar
          </button>
        </div>
      </div>

      {/* Tabela */}
      <div className="bg-white rounded-xl shadow-theme-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 text-gray-800 font-semibold">
            <ClipboardList className="h-5 w-5 text-brand-500" />
            Solicitações
          </div>
          <button type="button" onClick={() => refetch()} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pessoa</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Data</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Justificativa</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {isLoading ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Carregando...</td></tr>
              ) : solicitacoes.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Nenhuma solicitação encontrada.</td></tr>
              ) : (
                solicitacoes.map((s) => (
                  <tr key={s.idSolicitacaoAjusteAjudaCusto} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-gray-900 font-medium">{s.pessoaNome ?? `#${s.pessoaId}`}</td>
                    <td className="px-4 py-2 text-center text-gray-600">{formatarData(s.data)}</td>
                    <td className="px-4 py-2 text-gray-600 max-w-xs truncate" title={s.justificativa}>{s.justificativa}</td>
                    <td className="px-4 py-2 text-center">
                      <BadgeStatus status={s.status} descricao={s.statusDescricao} />
                      {s.status === StatusSolicitacao.Reprovada && s.motivoReprova && (
                        <p className="text-xs text-gray-400 mt-1" title={s.motivoReprova}>{s.motivoReprova}</p>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {(() => {
                        const pendente = s.status === StatusSolicitacao.Pendente
                        return (
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              disabled={!pendente || aprovar.isLoading}
                              onClick={() => aprovar.mutate(s.idSolicitacaoAjusteAjudaCusto)}
                              title="Aprovar"
                              className="p-1.5 text-green-600 bg-green-50 rounded-md hover:bg-green-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              disabled={!pendente}
                              onClick={() => { setReprovando(s); setMotivo('') }}
                              title="Reprovar"
                              className="p-1.5 text-red-600 bg-red-50 rounded-md hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        )
                      })()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal reprovar */}
      <Modal
        isOpen={!!reprovando}
        onClose={() => setReprovando(null)}
        title="Reprovar solicitação"
        subtitle={reprovando ? `${reprovando.pessoaNome ?? ''} — ${formatarData(reprovando.data)}` : undefined}
        icon={<X className="h-6 w-6 text-red-500" />}
        size="md"
        footer={
          <div className="flex justify-end gap-3 p-4">
            <button type="button" onClick={() => setReprovando(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
            <button
              type="button"
              disabled={!motivo.trim() || reprovar.isLoading}
              onClick={confirmarReprova}
              className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              Confirmar reprovação
            </button>
          </div>
        }
      >
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={4}
          placeholder="Motivo da reprovação..."
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </Modal>

      {/* Modal registro manual */}
      <Modal
        isOpen={manualAberto}
        onClose={() => setManualAberto(false)}
        title="Lançar registro manual"
        icon={<Plus className="h-6 w-6 text-brand-500" />}
        size="md"
        footer={
          <div className="flex justify-end gap-3 p-4">
            <button type="button" onClick={() => setManualAberto(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
            <button
              type="button"
              disabled={!manualPessoa || !manualData || lancarManual.isLoading}
              onClick={confirmarManual}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
            >
              Salvar
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Pessoa</label>
            <select value={manualPessoa} onChange={(e) => setManualPessoa(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Selecione...</option>
              {pessoas.map((p) => (
                <option key={p.idPessoa} value={p.idPessoa}>{p.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Data</label>
            <input type="date" value={manualData} onChange={(e) => setManualData(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
          </div>
        </div>
      </Modal>
    </div>
  )
}
